import { useMemo } from 'react';

import { useAllOrdersQuery } from '../orders/api';
import type { MerchantOrder } from '../orders/types';

import { paymentBucketOf, type PaymentBucket } from './types';

export interface PaymentBucketSummary {
  readonly bucket: PaymentBucket;
  readonly count: number;
  readonly amount: number;
  readonly orders: readonly MerchantOrder[];
}

export interface PaymentsOverview {
  readonly buckets: readonly PaymentBucketSummary[];
  /** Sum of `grandTotal` for orders whose payment is confirmed — the one figure honestly callable "montant encaissé". */
  readonly amountCollected: number;
  readonly truncated: boolean;
  readonly isPending: boolean;
  readonly isError: boolean;
}

const BUCKET_ORDER: readonly PaymentBucket[] = [
  'pending',
  'confirmed',
  'failed',
  'refunded',
  'cancelled',
];

/**
 * "Paiements & COD" — buckets every real order (`useAllOrdersQuery`) by
 * its payment-relevant status (`PAYMENT_BUCKET_BY_STATUS`). No read of
 * the `payments` table (no merchant route exists on it — see
 * `features/payments/types.ts`'s own header).
 */
export function usePaymentsOverview(): PaymentsOverview {
  const allOrders = useAllOrdersQuery();

  const buckets = useMemo<readonly PaymentBucketSummary[]>(() => {
    const grouped = new Map<PaymentBucket, MerchantOrder[]>();
    for (const order of allOrders.orders) {
      const bucket = paymentBucketOf(order);
      const list = grouped.get(bucket) ?? [];
      list.push(order);
      grouped.set(bucket, list);
    }

    return BUCKET_ORDER.map((bucket) => {
      const orders = grouped.get(bucket) ?? [];
      return {
        bucket,
        count: orders.length,
        amount: orders.reduce((sum, order) => sum + order.grandTotal, 0),
        orders: [...orders].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      };
    });
  }, [allOrders.orders]);

  const amountCollected = buckets.find((bucket) => bucket.bucket === 'confirmed')?.amount ?? 0;

  return {
    buckets,
    amountCollected,
    truncated: allOrders.truncated,
    isPending: allOrders.isPending,
    isError: allOrders.isError,
  };
}
